/**
 * PlantPhotoManager - Unified photo management for plants
 * Search trusted sources | Upload | Add by URL | Replace | Delete | Edit attribution
 * Scope: Plantaday views only (not designer plan area)
 */
import React, { useState } from 'react';
import { Search, Upload, Image as ImageIcon, Loader, Trash2, Pencil, Info } from 'lucide-react';
import { convertHeicToJpeg, isHeicFile } from '../utils/convertHeic';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

export function PlantPhotoManager({ plant, onSave, onClose, isAdmin }) {
  const [images, setImages] = useState(() => {
    const list = [];
    const plantData = plant?.plant_data || {};
    if (plant?.image_url) {
      list.push({ url: plant.image_url, photographer: plant.photo_attribution, isPrimary: true });
    }
    (plantData.additional_images || []).forEach(img => {
      const url = typeof img === 'string' ? img : img?.url;
      if (url && url !== plant?.image_url) {
        list.push({ url, photographer: typeof img === 'object' ? img.photographer : undefined, isPrimary: false });
      }
    });
    return list;
  });
  const [findingPhotos, setFindingPhotos] = useState(false);
  const [foundPhotos, setFoundPhotos] = useState([]);
  const [selectedFoundPhotos, setSelectedFoundPhotos] = useState([]);
  const [addByUrlValue, setAddByUrlValue] = useState('');
  const [addByUrlAttribution, setAddByUrlAttribution] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingAttributionIndex, setEditingAttributionIndex] = useState(null);
  const [editingAttributionValue, setEditingAttributionValue] = useState('');
  const [error, setError] = useState(null);

  const botanicalName = plant?.botanical_name || '';
  const commonName = plant?.common_name || '';
  const hasName = (botanicalName || commonName || '').trim().length > 0;

  const searchTrustedSources = async () => {
    if (!hasName) {
      const searchTerm = botanicalName || commonName || 'plant';
      window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(searchTerm)}`, '_blank');
      return;
    }
    setFindingPhotos(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/plants/find-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          botanicalName: botanicalName.trim(),
          commonName: commonName.trim() || null,
          verifiedOnly: true
        })
      });
      const data = await res.json();
      if (res.ok && data.photos?.length > 0) {
        const verified = data.photos.filter(p =>
          p.url && !p.url.includes('placeholder') &&
          (p.verified || ['wikimedia', 'inaturalist', 'kew', 'gbif', 'plantnet'].includes(p.source))
        );
        if (verified.length > 0) {
          setFoundPhotos(verified);
          setSelectedFoundPhotos([]);
          return;
        }
      }
      window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(botanicalName || commonName)}`, '_blank');
    } catch (err) {
      setError(err.message);
      window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(botanicalName || commonName)}`, '_blank');
    } finally {
      setFindingPhotos(false);
    }
  };

  const addSelectedPhotos = () => {
    const newImages = selectedFoundPhotos.map(url => {
      const p = foundPhotos.find(f => f.url === url);
      return { url, photographer: p?.attribution || p?.photographer, isPrimary: false };
    });
    setImages(prev => [...prev, ...newImages]);
    setFoundPhotos([]);
    setSelectedFoundPhotos([]);
  };

  const addByUrl = () => {
    const url = addByUrlValue.trim();
    if (!url) return;
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL.');
      return;
    }
    const isFirst = images.length === 0;
    setImages(prev => [...prev, {
      url,
      photographer: addByUrlAttribution.trim() || undefined,
      isPrimary: isFirst
    }]);
    setAddByUrlValue('');
    setAddByUrlAttribution('');
    setError(null);
  };

  const togglePhotoSelection = (url) => {
    setSelectedFoundPhotos(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]
    );
  };

  const setPrimary = (index) => {
    setImages(prev => prev.map((img, i) => ({ ...img, isPrimary: i === index })));
  };

  const removeImage = (index) => {
    setImages(prev => {
      const next = prev.filter((_, i) => i !== index);
      if (prev[index].isPrimary && next.length > 0) next[0].isPrimary = true;
      return next;
    });
  };

  const saveAttribution = (index) => {
    setImages(prev => prev.map((img, i) =>
      i === index ? { ...img, photographer: editingAttributionValue.trim() || undefined } : img
    ));
    setEditingAttributionIndex(null);
    setEditingAttributionValue('');
  };

  const handleUpload = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      if (isHeicFile(file)) {
        try {
          file = await convertHeicToJpeg(file);
        } catch (convErr) {
          throw new Error(convErr.message || 'Could not convert HEIC image.');
        }
      }
      const formData = new FormData();
      formData.append('image', file);
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/upload/plant-image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      const isFirst = images.length === 0;
      setImages(prev => [...prev, { url: data.imageUrl, photographer: undefined, isPrimary: isFirst }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const primaryImage = images.find(img => img.isPrimary);
      const additionalImages = images.filter(img => !img.isPrimary)
        .map(img => img.photographer ? { url: img.url, photographer: img.photographer } : img.url);

      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/plants/${plant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          image_url: primaryImage?.url || null,
          photo_attribution: primaryImage?.photographer || null,
          additional_images: additionalImages,
          replace_additional_images: true
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || 'Save failed');
      onSave?.(data.plant || data);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-4 sm:p-6 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Manage Photos</h3>
            <p className="text-sm text-gray-600">{plant?.common_name || plant?.botanical_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-2">×</button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{error}</div>
          )}

          {/* Actions: Search | Upload | Add by URL */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={searchTrustedSources}
              disabled={findingPhotos}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60 text-sm font-medium"
              title="Wikimedia, iNaturalist & verified sources first. If none found, Google opens."
            >
              {findingPhotos ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search trusted sources
            </button>
            <button
              type="button"
              onClick={() => window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(botanicalName || commonName || 'plant')}`, '_blank')}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              title="Open Google Images to find photos. Copy the image URL and use Add by URL above."
            >
              <ImageIcon className="w-4 h-4" />
              Search Google Images
            </button>
            <label className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer text-sm font-medium">
              {uploading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
              <input type="file" accept="image/*,image/heic,image/heif,.heic,.heif" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
            <div className="flex-1 flex gap-2 min-w-[200px]">
              <input
                type="url"
                value={addByUrlValue}
                onChange={(e) => setAddByUrlValue(e.target.value)}
                placeholder="Paste image URL"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <input
                type="text"
                value={addByUrlAttribution}
                onChange={(e) => setAddByUrlAttribution(e.target.value)}
                placeholder="Attribution (optional)"
                className="w-24 px-2 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={addByUrl}
                className="px-3 py-2 bg-emerald-100 text-emerald-800 rounded-lg hover:bg-emerald-200 text-sm font-medium"
              >
                Add
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1" title="Trusted sources (Wikimedia, iNaturalist, Kew) have verified botanical photos with attribution. Stock photos (Pexels/Unsplash) are used only if no trusted results.">
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            Trusted sources first (Wikimedia, iNaturalist, Kew). If none found, Google Images opens automatically.
          </p>

          {/* Photo picker modal (from search) */}
          {foundPhotos.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-700 mb-2">Select photos to add</p>
              <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                {foundPhotos.map((p) => (
                  <button
                    key={p.url}
                    type="button"
                    onClick={() => togglePhotoSelection(p.url)}
                    className={`relative rounded-lg overflow-hidden border-2 ${selectedFoundPhotos.includes(p.url) ? 'border-emerald-500' : 'border-gray-200'}`}
                  >
                    <img src={p.url} alt="" className="w-full h-16 object-cover" />
                    {selectedFoundPhotos.includes(p.url) && (
                      <div className="absolute inset-0 bg-emerald-500/30 flex items-center justify-center">
                        <span className="text-white text-xl">✓</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={addSelectedPhotos}
                  disabled={selectedFoundPhotos.length === 0}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm"
                >
                  Add {selectedFoundPhotos.length > 0 ? selectedFoundPhotos.length : ''} selected
                </button>
                <button type="button" onClick={() => setFoundPhotos([])} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Gallery */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Current photos</p>
            {images.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">No photos yet. Search, upload, or add by URL above.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className={`relative group rounded-lg overflow-hidden border-2 ${img.isPrimary ? 'border-emerald-500' : 'border-gray-200'}`}
                  >
                    <img src={img.url} alt="" className="w-full h-24 object-cover" onError={(e) => e.target.src = 'https://placehold.co/100x80/e5e7eb/6b7280?text=Error'} />
                    {img.isPrimary && <div className="absolute top-1 left-1 bg-emerald-600 text-white text-xs px-1.5 py-0.5 rounded">Primary</div>}
                    {img.photographer && (
                      <div className="absolute bottom-1 left-1 right-1 bg-black/60 text-white text-xs px-1 py-0.5 truncate rounded">
                        {editingAttributionIndex === idx ? (
                          <div className="flex gap-1">
                            <input
                              value={editingAttributionValue}
                              onChange={(e) => setEditingAttributionValue(e.target.value)}
                              className="flex-1 px-1 text-gray-900 text-xs"
                              autoFocus
                            />
                            <button type="button" onClick={() => saveAttribution(idx)} className="text-emerald-300">✓</button>
                            <button type="button" onClick={() => { setEditingAttributionIndex(null); setEditingAttributionValue(''); }} className="text-gray-300">×</button>
                          </div>
                        ) : (
                          <span title={img.photographer} onClick={() => { setEditingAttributionIndex(idx); setEditingAttributionValue(img.photographer || ''); }} className="cursor-pointer flex items-center gap-0.5">
                            {img.photographer} <Pencil className="w-3 h-3 opacity-70" />
                          </span>
                        )}
                      </div>
                    )}
                    {!img.photographer && editingAttributionIndex === idx ? (
                      <div className="absolute bottom-1 left-1 right-1 bg-black/60 p-1 rounded flex gap-1">
                        <input
                          value={editingAttributionValue}
                          onChange={(e) => setEditingAttributionValue(e.target.value)}
                          placeholder="Add attribution"
                          className="flex-1 px-1 text-gray-900 text-xs"
                          autoFocus
                        />
                        <button type="button" onClick={() => saveAttribution(idx)} className="text-emerald-300 text-xs">✓</button>
                        <button type="button" onClick={() => setEditingAttributionIndex(null)} className="text-gray-300 text-xs">×</button>
                      </div>
                    ) : (
                      !img.photographer && (
                        <button
                          type="button"
                          onClick={() => { setEditingAttributionIndex(idx); setEditingAttributionValue(''); }}
                          className="absolute bottom-1 right-1 bg-black/50 text-white p-1 rounded hover:bg-black/70"
                          title="Add attribution"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )
                    )}
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!img.isPrimary && (
                        <button type="button" onClick={() => setPrimary(idx)} className="bg-emerald-600 text-white p-1 rounded text-xs">Primary</button>
                      )}
                      <button type="button" onClick={() => removeImage(idx)} className="bg-red-600 text-white p-1 rounded">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || images.length === 0}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium"
            >
              {saving ? <><Loader className="w-4 h-4 animate-spin inline mr-2" />Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
